import React, {MutableRefObject, ReactElement, useImperativeHandle, useRef} from 'react';
import MaterialTable, {Column, Query, QueryResult} from '@material-table/core';
import TableIcons from './MaterialTableIcons';
import {NxMaterialTable, NxQuery, NxQueryResult, NxTableProps, NxTableRef} from './NxTableTypes';
import NxTableAction from './NxTableAction';
import styles from './NxTable.scss';
import clsx from 'clsx';
import {Box} from '@material-ui/core';
import NxTableActions from './NxTableActions';

/* eslint-disable @typescript-eslint/ban-types, @typescript-eslint/ban-ts-comment */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Container: React.ComponentType<unknown> = (props: {children: React.ReactNode}): React.ReactElement => props.children;

const adaptData = function<RowData extends object>(provider : (query: NxQuery) => Promise<NxQueryResult<RowData>>): (query: Query<RowData>) => Promise<QueryResult<RowData>> {
  return async (query: Query<RowData>): Promise<QueryResult<RowData>> => {
    try {
      const {result, pageNo, totalCount} = await provider({
        pageSize: query.pageSize,
        page: query.page
      });

      return {
        data: result,
        page: pageNo,
        totalCount: totalCount
      }
    } catch (error) {
      console.error('Error while fetching data', error);

      return {
        data: [],
        page: 0,
        totalCount: 0
      }
    }
  }
}


const WithMaterialTableRef = function<RowData, T extends {materialTableRef: MutableRefObject<NxMaterialTable<RowData> | null>}>(
  Component: React.ComponentType<T>,
  materialTableRef: MutableRefObject<NxMaterialTable<RowData> | null>): React.ComponentType<Omit<T, 'materialTableRef'>> {
  const Wrapper = (props: Omit<T, 'materialTableRef'>): React.ReactElement =>
    // @ts-ignore
    <Component {...props} materialTableRef={materialTableRef}/>

  return Wrapper;
}

/**
 * Table components capable of:
 * - sorting
 * - pagination
 */

function NxTable<RowData extends object> (props: NxTableProps<RowData>, ref: MutableRefObject<NxTableRef>) : ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actions = (props.rowActions ?? []).map(action => ({position: 'row', action})) as any;
  const rightAlignActions = (props.rowActions ?? []).length > 0;

  const columns = props.columns as unknown as Column<RowData>[];
  // @ts-ignore
  const data: RowData[] | ((query: Query<RowData>) => Promise<QueryResult<RowData>>) = Array.isArray(props.data) ? props.data : adaptData(props.data);

  const classes = clsx(
    styles.root,
    {
      [styles.actionCell_body]: rightAlignActions,
      [styles.actionCell_header]: rightAlignActions
    });

  const columnsWithDefaults: Column<RowData>[] =
    columns.some(column => column.defaultSort)
      ? columns
      : columns.map((value, index) => index === 0 ? {defaultSort: 'asc', ...value} : value);

  const localRef = useRef<NxMaterialTable<RowData> | null>(null);

  useImperativeHandle(ref, () => ({
    onQueryChange: () : void => {
      if(!localRef.current) {
        return;
      }
      if(!localRef.current.isRemoteData()) {
        throw new Error('onQueryChange can only be called for remote data')
      }
      localRef.current.onQueryChange();
    }
  }));

  return <Box className={classes} ml={props.negativeMargin ?? -2} mr={props.negativeMargin ?? -2}>
    <MaterialTable
      tableRef={localRef}
      icons={TableIcons}
      components={{
        Action: NxTableAction,
        Actions: WithMaterialTableRef(NxTableActions, localRef),
        // remove paper from container
        Container: Container
      }}
      // @ts-ignore
      data={data}
      isLoading={props.isLoading}
      columns={columnsWithDefaults}
      actions={actions}
      options={{
        sorting: props.sortable ?? Array.isArray(data),
        loadingType: 'linear',
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        exportAllData: true,
        draggable: false,
        pageSize: 25,
        pageSizeOptions: [25, 50],
        search: false,
        showTitle: false,
        toolbarButtonAlignment: 'left',
        toolbar: false,
        tableLayout: 'auto',
        headerStyle: {
          background: 'unset'
        },
        selection: props.selection ?? false
      }}
      onSelectionChange={props.onSelectionChange}
    />
  </Box>;
}

export default React.forwardRef(NxTable) as <RowData extends object>(props: NxTableProps<RowData> & { ref?: MutableRefObject<NxTableRef | null> }) => ReactElement;
export type {NxTableAction, NxTableActionProps, NxTableProps, NxTableColumn, NxQuery, NxQueryResult, NxTableRef} from './NxTableTypes';
export {NxTableColumnAlignment, NxTableColumnType } from './NxTableTypes';